import React from 'react';
import { useNavigate } from 'react-router-dom';

const RoomNotFound = () => {
    const navigate = useNavigate();

    const handleGoHome = () => {
        navigate('/dashboard');
    };

    return (
        <div className="bg-[linear-gradient(to_top,_#38275B_0%,_#251A3C_33.33333333333333%,_#140E20_66.66666666666666%,_#000000_100%)] min-h-screen max-h-screen relative overflow-hidden flex items-center justify-center p-4">
            {/* Cosmic background effects */}
            <div className="absolute inset-0">
                {/* Stars effect */}
                <div className="absolute top-10 left-10 w-1 h-1 bg-white rounded-full opacity-60 animate-pulse"></div>
                <div className="absolute top-20 right-20 w-1 h-1 bg-white rounded-full opacity-40 animate-pulse delay-1000"></div>
                <div className="absolute top-32 left-1/4 w-1 h-1 bg-white rounded-full opacity-50 animate-pulse delay-2000"></div>
                <div className="absolute top-40 right-1/3 w-1 h-1 bg-white rounded-full opacity-30 animate-pulse delay-3000"></div>
                <div className="absolute top-60 left-1/2 w-1 h-1 bg-white rounded-full opacity-60 animate-pulse delay-4000"></div>
                <div className="absolute top-80 right-1/4 w-1 h-1 bg-white rounded-full opacity-40 animate-pulse delay-5000"></div>
                <div className="absolute top-96 left-1/3 w-1 h-1 bg-white rounded-full opacity-50 animate-pulse delay-6000"></div>

                {/* Additional stars */}
                <div className="absolute top-1/4 left-1/5 w-0.5 h-0.5 bg-white rounded-full opacity-30"></div>
                <div className="absolute top-1/3 right-1/5 w-0.5 h-0.5 bg-white rounded-full opacity-40"></div>
                <div className="absolute top-1/2 left-1/6 w-0.5 h-0.5 bg-white rounded-full opacity-35"></div>
                <div className="absolute top-2/3 right-1/6 w-0.5 h-0.5 bg-white rounded-full opacity-45"></div>
                <div className="absolute top-3/4 left-1/4 w-0.5 h-0.5 bg-white rounded-full opacity-30"></div>
            </div>

            {/* Main content */}
            <div className="relative z-10 text-center px-4 max-w-2xl mx-auto w-full overflow-y-auto max-h-full">
                {/* 404 Icon */}
                <div className="mb-4 sm:mb-6 mt-[100px]">
                    <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 bg-red-500/20 rounded-full mb-4">
                        <svg
                            className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 text-red-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                        </svg>
                    </div>
                </div>

                {/* Error Code */}
                <div className="mb-4 sm:mb-6">
                    <h1 className="text-4xl sm:text-6xl md:text-8xl font-bold text-white mb-2 sm:mb-4 opacity-90">
                        404
                    </h1>
                    <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-red-500 to-purple-500 mx-auto rounded-full"></div>
                </div>

                {/* Main Message */}
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2 sm:mb-4">
                        Meeting Not Found
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed">
                        The meeting you're looking for doesn't exist or has been removed.
                        It might have expired or the link is incorrect.
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-4 sm:mb-6">
                    <button
                        onClick={handleGoHome}
                        className="px-6 sm:px-8 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center text-sm sm:text-base"
                    >
                        <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                            />
                        </svg>
                        Go to Dashboard
                    </button>
                </div>

                {/* Support Info */}
                <div className="mt-6 sm:mt-8 text-center">
                    <p className="text-gray-400 text-xs sm:text-sm">
                        Need help? Contact our support team
                    </p>
                    <a
                        href="mailto:support@r-link.com"
                        className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm underline"
                    >
                        support@r-link.com
                    </a>
                </div>
            </div>

            {/* Floating elements for visual interest */}
            <div className="absolute top-1/4 left-8 w-2 h-2 bg-purple-400 rounded-full opacity-60 animate-bounce delay-1000"></div>
            <div className="absolute top-1/3 right-12 w-1 h-1 bg-blue-400 rounded-full opacity-40 animate-bounce delay-2000"></div>
            <div className="absolute bottom-1/4 left-16 w-1.5 h-1.5 bg-green-400 rounded-full opacity-50 animate-bounce delay-3000"></div>
            <div className="absolute bottom-1/3 right-8 w-1 h-1 bg-yellow-400 rounded-full opacity-30 animate-bounce delay-4000"></div>
        </div>
    );
};

export default RoomNotFound;
